import { Widget } from "../lib/widget"
import { $ } from "../lib/dom"

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content || ""
}

async function postJson(url, body = {}) {
  const response = await fetch(url, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Accept": "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken()
    },
    body: JSON.stringify(body)
  })
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(payload.error || `Request failed (${response.status})`)
  return payload
}

class SubmissionComponentsView extends Widget {
  constructor(options = {}) {
    super()
    this.documentView = options.documentView
    this.form = this.$("#submission-components-form")
    this.gradeButton = this.$("#grade-with-ai-button")
    this.gradeLoading = this.$("#grade-with-ai-loading")
    this.delegate("click", ".select-all .btn", this.selectAll)
    this.delegate("click", "#expand-all", this.expandAll)
    this.delegate("click", "#collapse", this.collapse)
    this.delegate("click", ".add-comment", this.showComment)
    this.delegate("click", ".view-document", this.showDocumentPreview)
    this.delegate("click", "#resubmission-toggle", this.toggleResubmissionForm)
    this.delegate("click", "#grade-with-ai-button", this.gradeWithAi)
  }

  selectAll(event, button) {
    event.preventDefault()
    event.stopPropagation()
    const score = $(button).data("score")
    if (score !== undefined) {
      this.form.find(`label.score-option[data-score='${score}']:not(.active)`).each((_, label) => {
        label.closest(".btn-group")?.querySelectorAll("label.score-option")
          .forEach((option) => option.classList.remove("active"))
        label.classList.add("active")
        const input = label.querySelector("input")
        if (input) input.checked = true
      })
    }
  }

  expandAll(event, link) {
    event.preventDefault()
    this.$(".scroll-wrapper").addClass("disabled")
    $(link).find("small").text("(Collapse)")
    link.id = "collapse"
    link.title = "Enable Scrolling"
  }

  collapse(event, link) {
    event.preventDefault()
    this.$(".scroll-wrapper").removeClass("disabled")
    $(link).find("small").text("(Expand All)")
    link.id = "expand-all"
    link.title = "Disable Scrolling"
  }

  showComment(event, link) {
    event.preventDefault()
    $(link).closest(".hide-comment").removeClass("hide-comment").find("textarea.comment").trigger("focus")
  }

  showDocumentPreview(event, link) {
    event.preventDefault()
    this.$(".view-document").removeClass("active")
    link.classList.add("active")
    this.documentView.setFileId(link.dataset.fileId)
    const container = document.querySelector(this.documentView.container)
    if (container) window.scrollTo({ top: container.getBoundingClientRect().top + window.scrollY - 50, behavior: "smooth" })
  }

  toggleResubmissionForm(event) {
    event.preventDefault()
    const form = this.$("#resubmission-form").toggle()
    if (form.is(":visible")) {
      this.$("textarea[name='submission[comment]']").trigger("focus")
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" })
    }
  }

  async gradeWithAi(event, button) {
    event.preventDefault()
    if (button.disabled) return
    const endpoint = button.dataset.endpoint
    if (!endpoint) return this.showError("AI grading is unavailable right now.")
    this.setAiGradingState(true)
    try {
      const response = await postJson(endpoint)
      ;(response.components || []).forEach((component) => this.applyAiGrade(component))
    } catch (error) {
      this.showError(error.message || "AI grading failed. Please try again.")
    } finally {
      this.setAiGradingState(false)
    }
  }

  applyAiGrade(component) {
    const componentId = component.submission_component_id
    const scoreName = `submission_components[${componentId}][score]`
    const commentName = `submission_components[${componentId}][comment]`
    const scoreInputs = this.form.find(`input[name='${scoreName}']`)
    const selectedScore = this.form.find(`input[name='${scoreName}'][value='${component.score}']`)
    const commentField = this.form.find(`textarea[name='${commentName}']`)
    scoreInputs.prop("checked", false).closest("label.score-option").removeClass("active")
    selectedScore.prop("checked", true).closest("label.score-option").addClass("active")
    commentField.val(component.comment).closest(".comment-wrapper").removeClass("hide-comment")
  }

  setAiGradingState(loading) {
    if (this.gradeButton.length) {
      this.gradeButton.prop("disabled", loading)
      if (loading) {
        this.gradeButton.data("original-html", this.gradeButton.html()).text("Grading...")
      } else {
        this.gradeButton.html(this.gradeButton.data("original-html") || "Grade With AI")
      }
    }
    this.gradeLoading.toggle(loading)
    this.form.toggleClass("is-ai-grading", loading).find(":input").prop("disabled", loading)
  }

  showError(message) {
    window.alert(message)
  }
}

class SubmissionAiHelper extends Widget {
  constructor(options = {}) {
    super()
    this.submission = options.submission
    this.button = this.$("#ai-help-button")
    this.loadingIndicator = this.$("#ai-help-loading")
    this.form = this.$("#submission-form")
    this.overallFeedback = this.$("#revision-coach-overall")
    this.revisionReminder = this.$("#revision-coach-revision-reminder")
    this.defaultTitle = "Use Revision Coach to get feedback without changing your answers."
    this.lockedDisabled = this.button.is(":disabled") && Boolean(this.button.data("endpoint"))
    this.delegate("click", "#ai-help-button", this.requestHelp)
    this.delegate("input", "#submission-form textarea[data-rubric-component-id]:not([readonly])", this.refreshAvailability)
    this.delegate("change", "#submission-form textarea[data-rubric-component-id]:not([readonly])", this.refreshAvailability)
    ;["change:id", "change:box_file_id", "change:url", "sync"].forEach((event) => {
      this.submission?.on(event, this.refreshAvailability, this)
    })
    this.refreshAvailability()
  }

  async requestHelp(event) {
    event.preventDefault()
    if (this.button.is(":disabled")) return
    const endpoint = this.button.data("endpoint")
    if (!endpoint) return this.showError("Please upload your document before using Revision Coach.")
    const responses = this.componentResponses()
    if (!responses.length) return this.showError("There are no rubric response fields for Revision Coach to review.")

    this.setLoading(true)
    this.clearFeedback()
    try {
      const response = await postJson(endpoint, { component_responses: responses })
      this.showCoaching(response)
      this.used = true
    } catch (error) {
      this.showError(error.message || "Revision Coach failed. Please try again.")
    } finally {
      this.setLoading(false)
    }
  }

  editableResponseFields() {
    return this.form.find("textarea[data-rubric-component-id]:not([readonly])")
  }

  componentResponses() {
    return this.editableResponseFields().toArray().flatMap((field) => {
      const id = Number($(field).data("rubric-component-id"))
      return Number.isFinite(id) ? [{ rubric_component_id: id, body: field.value }] : []
    })
  }

  allEditableResponsesEntered() {
    const fields = this.editableResponseFields().toArray()
    return fields.length > 0 && fields.every((field) => field.value.trim() !== "")
  }

  showCoaching(response) {
    if (response.overall_feedback && this.overallFeedback.length) {
      this.overallFeedback.html(this.formatFeedback(response.overall_feedback)).show()
    }
    ;(response.components || []).forEach((component) => {
      const feedback = this.form.find(`.revision-coach-feedback[data-rubric-component-id='${component.rubric_component_id}']`)
      if (feedback.length) feedback.html(this.formatComponentFeedback(component)).show()
    })
    if (response.components?.length) this.revisionReminder.show()
  }

  clearFeedback() {
    this.overallFeedback.empty().hide()
    this.form.find(".revision-coach-feedback").empty().hide()
    this.revisionReminder.hide()
  }

  formatComponentFeedback(component) {
    let body = component.body || ""
    const progress = component.progress_indicator || ""
    let html = ""
    if (progress) {
      html += $("<div>").addClass("revision-coach-feedback__progress").text(progress)[0].outerHTML
      body = body.replace(new RegExp(`^Progress:\\s*${this.escapeRegExp(progress)}\\s*\\r?\\n?`, "i"), "")
    }
    return html + this.formatFeedback(body)
  }

  formatFeedback(text) {
    return String(text || "").split(/\r?\n/).filter((line) => line.trim()).map((line) => $("<div>").text(line).html()).join("<br>")
  }

  endpoint() {
    if (!this.submission?.id || !(this.submission.get("box_file_id") || this.submission.get("url"))) return null
    return `${this.submission.url()}/ai_assist`
  }

  refreshAvailability() {
    if (!this.button.length || this.lockedDisabled || this.loading) return
    if (this.used) {
      this.button.prop("disabled", true)
      return this.updateTooltip("Revision Coach has already been used for this submission.")
    }
    const endpoint = this.endpoint()
    const responsesEntered = this.allEditableResponsesEntered()
    this.button.data("endpoint", endpoint || "").prop("disabled", !(endpoint && responsesEntered))
    if (!endpoint) this.updateTooltip("Please upload your document before using Revision Coach.")
    else if (!responsesEntered) this.updateTooltip("Complete all rubric response fields before using Revision Coach.")
    else this.updateTooltip(this.defaultTitle)
  }

  setLoading(loading) {
    this.loading = loading
    this.button.prop("disabled", loading)
    this.loadingIndicator.toggle(loading)
    if (loading) this.button.data("original-text", this.button.text()).text("Reviewing...")
    else {
      this.button.text(this.button.data("original-text") || "Get Revision Coaching")
      this.refreshAvailability()
    }
  }

  updateTooltip(message) {
    this.button.attr({ title: message, "data-bs-original-title": message })
  }

  showError(message) {
    window.alert(message)
  }

  escapeRegExp(text) {
    return String(text).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  }
}

Object.assign(window, { SubmissionComponentsView, SubmissionAiHelper })
